import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Table, Row, Col, Button, Modal } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FaArrowLeft, FaHistory, FaMapMarkerAlt, FaRoute, FaUndo } from 'react-icons/fa';

// style
import s from './ViewRideBookingDetails.css';

// helpers
import messages from '../../../../locale/messages';
import { bookingStatusLocale } from '../../../../helpers/bookingStatusLocale';
import { getAvatarUrl, getUiAvatarUrl } from '../../../../utils/getAvatarUrl';
import { wita } from '../../../../helpers/witaTime';

// components
import Link from '../../../Link';
import CurrencyConverter from '../../../CurrencyConverter';

// redux actions
import deleteRideBooking from '../../../../actions/siteadmin/Booking/deleteRideBooking';
import restoreDeletedRideBooking from '../../../../actions/siteadmin/Booking/restoreDeletedRideBooking';

const isDefined = value => value !== null && value !== undefined && value !== '';

function InfoRow({ label, children }) {
  if (!label || !isDefined(children)) {
    return null;
  }

  return (
    <tr>
      <td className={s.tableLabelColumn}><b>{label}</b></td>
      <td className={s.lineBreak}>{children}</td>
    </tr>
  );
}

function AmountRow({ label, amount, currency, isMinus = false, isBold = false, afterContent = null }) {
  if (!label || !isDefined(amount) || !currency) {
    return null;
  }

  const value = (
    <>
      {isMinus ? '-' : ''}
      <CurrencyConverter from={currency} amount={amount} />
      {afterContent ? ` (${afterContent})` : ''}
    </>
  );

  return (
    <tr className={amount <= 0 ? 'text-muted' : ''}>
      <td className={s.tableLabelColumn}>{isBold ? <b>{label}</b> : label}</td>
      <td>
        {isBold
          ? <b>{value}</b>
          : <span className={cx({ 'text-danger': isMinus })}>{value}</span>
        }
      </td>
    </tr>
  );
}

function PersonRow({ label, person, profileLink }) {
  if (!person) {
    return null;
  }

  const fullName = `${person.firstName || ''} ${person.lastName || ''}`.trim();

  return (
    <tr>
      <td className={s.tableLabelColumn}><b>{label}</b></td>
      <td>
        <div className='d-flex align-items-center'>
          <img
            src={person.picture ? getAvatarUrl(person.picture) : getUiAvatarUrl(fullName)}
            alt='avatar'
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', marginRight: 12, flexShrink: 0 }}
          />
          <div>
            {profileLink && person.profileId
              ? <Link to={`${profileLink}/${person.profileId}`}>{fullName}</Link>
              : fullName
            }
            <br />
            {person.phoneNumber}
            <br />
            {person.email}
          </div>
        </div>
      </td>
    </tr>
  );
}

function MapLinkRow({ label, location, lat, lng }) {
  if (!location) {
    return null;
  }

  return (
    <tr>
      <td className={s.tableLabelColumn}><b>{label}</b></td>
      <td className={s.lineBreak}>
        <FaMapMarkerAlt className='text-danger mr-1' style={{ width: '1em', height: '1em' }} />
        {isDefined(lat) && isDefined(lng)
          ? <a href={`https://maps.google.com/?q=${lat},${lng}`} target='_blank' rel='noreferrer'>{location}</a>
          : location
        }
      </td>
    </tr>
  );
}

// Same Google Maps directions link the food booking view shows, so a ride's
// route can be inspected without pasting coordinates by hand.
function routeDirectionsUrl(booking) {
  const hasCoordinates = [booking.pickUpLat, booking.pickUpLng, booking.dropOffLat, booking.dropOffLng]
    .every(isDefined);

  if (!hasCoordinates) {
    return null;
  }

  return `https://www.google.com/maps/dir/${booking.pickUpLat},${booking.pickUpLng}/${booking.dropOffLat},${booking.dropOffLng}/`;
}

// Rows of the ride audit log. Only rendered when there are any: rides booked
// before the log shipped have none, and the button is hidden for those.
function ActivityTimeline({ activities }) {
  return (
    <ul className={s.timeline}>
      {activities.map((activity) => {
        const at = wita(activity.createdAt);

        return (
          <li key={activity.id}>
            <div className='d-flex justify-content-between align-items-center mb-1'>
              <div>
                <h6 className='mb-0 font-weight-bold'>{activity.label}</h6>
                {at && <small className='text-muted'>({at.fromNow()})</small>}
              </div>
              <div>
                <small className='text-muted'>
                  {at && at.format('DD MMM YYYY, HH:mm:ss')}
                </small>
              </div>
            </div>
            {activity.notes && <p className='mb-0 text-secondary'>{activity.notes}</p>}
          </li>
        );
      })}
    </ul>
  );
}

// Ride comfort flags are TINYINT on RideBooking.
function comfortOptions(booking, formatMessage) {
  const options = [];
  if (booking.isChildSeatAvailable) options.push(formatMessage(messages.childSeat));
  if (booking.isWheelChairAvailable) options.push(formatMessage(messages.wheelChair));
  if (booking.isWomenOnlyDriver) options.push(formatMessage(messages.womenOnlyDrivers));
  return options.join(', ');
}

export function ViewRideBookingDetails(props) {
  const { data, listSearch, intl, refetch } = props;
  const { formatMessage, locale } = intl;
  const booking = data && data.result;

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isRestoreModalOpen, setRestoreModalOpen] = useState(false);
  const [isActivityModalOpen, setActivityModalOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState('');
  const [isDeleting, setDeleting] = useState(false);
  const [isRestoring, setRestoring] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    const result = await props.deleteRideBooking(booking.id);
    if (result && result.status === 200 && refetch) {
      await refetch();
    }
    setDeleting(false);
    setDeleteModalOpen(false);
    setIdToDelete('');
  };

  const handleRestore = async () => {
    setRestoring(true);
    const result = await props.restoreDeletedRideBooking(booking.id);
    if (result && result.status === 200 && refetch) {
      await refetch();
    }
    setRestoring(false);
    setRestoreModalOpen(false);
  };

  if (!booking) {
    return (
      <div className='text-center p-4'>
        {(data && data.errorMessage) || formatMessage(messages.notFound)}
      </div>
    );
  }

  const goBackLink = `/siteadmin/bookings${listSearch || ''}`;
  const activities = booking.activities || [];
  const hasActivities = activities.length > 0;
  const distanceUnit = booking.distanceType === 'km'
    ? formatMessage(messages.kmLabel)
    : formatMessage(messages.milesLabel);
  const mapDirectionUrl = routeDirectionsUrl(booking);

  // Both service fees are platform commission; a promo trip is funded out of
  // them, and eats into the platform's own share once it exceeds them.
  const grossEarnings = Number(booking.riderServiceFee || 0) + Number(booking.driverServiceFee || 0);
  const discount = booking.isSpecialTrip ? Number(booking.specialTripPrice || 0) : 0;
  const platformEarnings = Math.max(0, grossEarnings - discount);
  const platformOverdraft = Math.max(0, discount - grossEarnings);

  return (
    <div>
      <Row className='align-items-center'>
        <Col lg={4} md={4} sm={4} xs={4}>
          <Link to={goBackLink} className={cx('btn btn-light')}>
            <FaArrowLeft />
            <span className='d-none d-md-inline-block ml-2'>
              <FormattedMessage {...messages.goBack} />
            </span>
          </Link>
        </Col>
        <Col lg={4} md={4} sm={4} xs={4}>
          <h1 className={cx(s.titleTextAdmin, s.space2, s.textAlignCenter)}>
            <FormattedMessage {...messages.rideBookingDetails} />
            <span className='ml-2'>#{booking.id}</span>
          </h1>
        </Col>
        <Col lg={4} md={4} sm={4} xs={4} className='text-right'>
          {hasActivities && (
            <Button variant='light' onClick={() => setActivityModalOpen(true)}>
              <FaHistory />
              <span className='d-none d-md-inline-block ml-2'>Ride Activities</span>
            </Button>
          )}
        </Col>
      </Row>

      {booking.deletedAt && (
        <div className='alert alert-danger p-2 m-2 text-center'>
          Ride deleted <b title={booking.deletedAt}>{moment(booking.deletedAt).fromNow()}</b>
          <button
            className='btn btn-light btn-sm ml-2'
            onClick={() => setRestoreModalOpen(true)}
            type='button'
            title='Restore ride'
          >
            <FaUndo className='mr-2' />
            Restore
          </button>
        </div>
      )}

      <Modal show={isDeleteModalOpen} onHide={() => setDeleteModalOpen(false)}>
        <Modal.Header>
          <Modal.Title>Delete Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Enter the Booking ID to delete this ride: <code>{booking.id}</code>
          </p>
          <input
            type='text'
            className='form-control'
            placeholder='Booking ID to be deleted'
            value={idToDelete}
            onChange={e => setIdToDelete(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant='danger'
            disabled={Number(idToDelete) !== booking.id || isDeleting}
            onClick={handleDelete}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isRestoreModalOpen} onHide={() => setRestoreModalOpen(false)}>
        <Modal.Header>
          <Modal.Title>Restore Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to restore this ride booking?</p>
          <p>Booking ID: <code>{booking.id}</code></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setRestoreModalOpen(false)}>
            Cancel
          </Button>
          <Button variant='primary' disabled={isRestoring} onClick={handleRestore}>
            {isRestoring ? 'Restoring...' : 'Restore'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isActivityModalOpen && hasActivities} onHide={() => setActivityModalOpen(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHistory className='mr-2' />
            Ride Activities
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ActivityTimeline activities={activities} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setActivityModalOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <div className='table-responsive'>
        <Table>
          <tbody>
            <InfoRow label={formatMessage(messages.orderId)}>{`#${booking.id}`}</InfoRow>
            <PersonRow
              label={formatMessage(messages.riderDetails)}
              person={booking.riderDetails}
              profileLink='/siteadmin/users'
            />
            <PersonRow
              label={formatMessage(messages.driverDetails)}
              person={booking.driverDetails}
              profileLink='/siteadmin/drivers'
            />
            <MapLinkRow
              label={formatMessage(messages.pickUpLocation)}
              location={booking.pickUpLocation}
              lat={booking.pickUpLat}
              lng={booking.pickUpLng}
            />
            <InfoRow label={formatMessage(messages.totalRideDistance)}>
              <>
                <FaRoute className='text-muted mr-1' style={{ width: '1em', height: '1em' }} />
                {`${booking.totalRideDistance || 0} ${distanceUnit} `}
                {mapDirectionUrl && (
                  <>
                    (
                    <a href={mapDirectionUrl} target='_blank' rel='noreferrer' className='btn btn-link btn-sm'>
                      View Route
                    </a>
                    )
                  </>
                )}
              </>
            </InfoRow>
            <MapLinkRow
              label={formatMessage(messages.dropLocation)}
              location={booking.dropOffLocation}
              lat={booking.dropOffLat}
              lng={booking.dropOffLng}
            />
            <InfoRow label={formatMessage(messages.tripStatus)}>
              {bookingStatusLocale(booking.tripStatus, locale)}
            </InfoRow>
            {booking.cancelReason && (
              <InfoRow label={formatMessage(messages.cancelReason)}>
                {booking.cancelReason.reason}
              </InfoRow>
            )}
            <InfoRow label={formatMessage(messages.categoryName)}>
              {booking.categoryDetails && booking.categoryDetails.categoryName}
            </InfoRow>
            <InfoRow label={formatMessage(messages.vehicleNumber)}>{booking.vehicleNumber}</InfoRow>
            <InfoRow label={formatMessage(messages.rideComfortOptions)}>
              {comfortOptions(booking, formatMessage)}
            </InfoRow>
            <InfoRow label={formatMessage(messages.totalDuration)}>
              {isDefined(booking.totalDuration) ? `${Math.round(booking.totalDuration)} min` : null}
            </InfoRow>
            <InfoRow label={formatMessage(messages.bookingCreatedAt)}>
              {booking.createdAt && moment(new Date(booking.createdAt).toISOString()).format('DD-MM-YYYY HH:mm')}
            </InfoRow>
            <InfoRow label={formatMessage(messages.tripStart)}>
              {booking.tripStart && moment(new Date(booking.tripStart).toISOString()).format('DD-MM-YYYY HH:mm')}
            </InfoRow>
            <InfoRow label={formatMessage(messages.tripEnd)}>
              {booking.tripEnd && moment(new Date(booking.tripEnd).toISOString()).format('DD-MM-YYYY HH:mm')}
            </InfoRow>
            <InfoRow label={formatMessage(messages.paymentType)}>{booking.paymentName}</InfoRow>
            <InfoRow label={formatMessage(messages.ridePaymentStatus)}>
              {booking.xenditChargeStatus || booking.paymentStatus}
            </InfoRow>
            <InfoRow label={formatMessage(messages.confirmationCode)}>{booking.confirmationCode}</InfoRow>
            <InfoRow label={formatMessage(messages.rideNotes)}>{booking.notes}</InfoRow>
          </tbody>
        </Table>
      </div>

      <h2 className={s.sectionHeading}><FormattedMessage {...messages.riderBillingInfo} /></h2>
      <div className='table-responsive'>
        <Table>
          <tbody>
            <AmountRow label={formatMessage(messages.rideFare)} amount={booking.totalFare} currency={booking.currency} />
            <AmountRow label={formatMessage(messages.waitingFare)} amount={booking.waitingPrice} currency={booking.currency} />
            <AmountRow label={formatMessage(messages.riderServiceFare)} amount={booking.riderServiceFee} currency={booking.currency} />
            {discount > 0 && (
              <AmountRow label={formatMessage(messages.riderDiscount)} amount={discount} currency={booking.currency} isMinus />
            )}
            {booking.tollFee > 0 && (
              <AmountRow label={formatMessage(messages.tollFee)} amount={booking.tollFee} currency={booking.currency} />
            )}
            {booking.tipsAmount > 0 && (
              <AmountRow label={formatMessage(messages.tipsAmount)} amount={booking.tipsAmount} currency={booking.currency} />
            )}
            <AmountRow
              label={formatMessage(messages.riderPayable)}
              amount={Math.round(booking.riderPayableFare || booking.totalFare || 0)}
              currency={booking.currency}
              isBold
            />
          </tbody>
        </Table>
      </div>

      <h2 className={s.sectionHeading}><FormattedMessage {...messages.driverBillingInfo} /></h2>
      <div className='table-responsive'>
        <Table>
          <tbody>
            <AmountRow label={formatMessage(messages.rideFare)} amount={booking.totalFare} currency={booking.currency} />
            <AmountRow label={formatMessage(messages.waitingFare)} amount={booking.waitingPrice} currency={booking.currency} />
            <AmountRow label={formatMessage(messages.driverServiceFee)} amount={booking.driverServiceFee} currency={booking.currency} isMinus />
            {booking.tollFee > 0 && (
              <AmountRow label={formatMessage(messages.tollFee)} amount={booking.tollFee} currency={booking.currency} />
            )}
            {booking.tipsAmount > 0 && (
              <AmountRow label={formatMessage(messages.tipsAmount)} amount={booking.tipsAmount} currency={booking.currency} />
            )}
            <AmountRow
              label={formatMessage(messages.driverEarnings)}
              amount={Math.round(booking.driverTotalFare || 0)}
              currency={booking.currency}
              isBold
            />
          </tbody>
        </Table>
      </div>

      <h2 className={s.sectionHeading}><FormattedMessage {...messages.platformEarnings} /></h2>
      <div className='table-responsive'>
        <Table>
          <tbody>
            <AmountRow label={formatMessage(messages.riderServiceFare)} amount={booking.riderServiceFee} currency={booking.currency} />
            <AmountRow label={formatMessage(messages.driverServiceFee)} amount={booking.driverServiceFee} currency={booking.currency} />
            {discount > 0 && (
              <AmountRow label={formatMessage(messages.riderDiscount)} amount={discount} currency={booking.currency} isMinus />
            )}
            <AmountRow
              label={formatMessage(messages.adminEarning)}
              amount={Math.round(platformEarnings)}
              currency={booking.currency}
              isBold
            />
            {platformOverdraft > 0 && (
              <AmountRow
                label={formatMessage(messages.riderDiscount)}
                amount={Math.round(platformOverdraft)}
                currency={booking.currency}
                isMinus
              />
            )}
          </tbody>
        </Table>
      </div>

      {!booking.deletedAt && (
        <button
          className='btn btn-link text-danger'
          onClick={() => setDeleteModalOpen(true)}
          type='button'
          title='Delete ride booking'
        >
          Delete Ride Booking
        </button>
      )}
    </div>
  );
}

ViewRideBookingDetails.propTypes = {
  data: PropTypes.object,
  listSearch: PropTypes.string,
  refetch: PropTypes.func,
  deleteRideBooking: PropTypes.func.isRequired,
  restoreDeletedRideBooking: PropTypes.func.isRequired,
};

const mapState = () => ({});

const mapDispatch = {
  deleteRideBooking,
  restoreDeletedRideBooking,
};

export default compose(
  injectIntl,
  withStyles(s),
  connect(mapState, mapDispatch),
)(ViewRideBookingDetails);
